import React from "react";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, ExternalLink } from "lucide-react";
import { SiLine, SiUbereats } from "react-icons/si";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/contexts/SettingsContext";
import { motion } from "framer-motion";

const DAY_ORDER = [
  ["monday", "mon"], ["tuesday", "tue"], ["wednesday", "wed"], ["thursday", "thu"],
  ["friday", "fri"], ["saturday", "sat"], ["sunday", "sun"],
];

export default function ContactPage() {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const s = settings || {};
  const hours = s.hours_by_day || {};

  const links = [
    { label: "Instagram", url: s.instagram_url, Icon: Instagram, color: "#E1306C" },
    { label: "Facebook", url: s.facebook_url, Icon: Facebook, color: "#1877F2" },
    { label: "LINE", url: s.line_url, Icon: SiLine, color: "#06C755" },
    { label: "Uber Eats", url: s.uber_url, Icon: SiUbereats, color: "#06C167" },
    { label: "Google Reviews", url: s.google_review_url, Icon: ExternalLink, color: "#C9A227" },
  ];

  return (
    <div className="pt-32 pb-12 min-h-screen" data-testid="contact-page">
      <div className="container-tulsi">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.4em] uppercase text-[#D11D21] mb-3">{t("contact.subtitle")}</p>
          <h1 className="font-serif-display text-6xl text-gold-gradient">{t("contact.title")}</h1>
          <div className="gold-separator" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="glass-card p-8 space-y-5">
            <h3 className="font-serif-display text-2xl text-[#2A0708] mb-2">{t("contact.reachUs")}</h3>
            <Row icon={Phone} label={t("contact.phone")} value={s.phone} href={`tel:${s.phone}`} testId="contact-phone" />
            <Row icon={Mail} label={t("contact.email")} value={s.email} href={`mailto:${s.email}`} testId="contact-email" />
            <Row icon={MapPin} label={t("contact.address")} value={s.address} testId="contact-address" />
            <div className="flex items-start gap-3" data-testid="contact-hours">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#E8D9BE] flex-shrink-0">
                <Clock size={16} className="text-[#C9A227]" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#D11D21] mb-2">{t("contact.hours")}</p>
                <ul className="space-y-1 text-sm">
                  {DAY_ORDER.map(([day, sk]) => {
                    const isClosed = (hours[day] || "").toLowerCase() === "closed";
                    return (
                      <li key={day} className="flex justify-between gap-3" data-testid={`day-${day}`}>
                        <span className="text-[#6B4A4E] uppercase tracking-wider text-xs w-20">{t(`days.${sk}`)}</span>
                        <span className={`flex-1 text-right ${isClosed ? "text-red-400" : "text-[#2A0708]"}`}>
                          {isClosed ? t("days.closed") : (hours[day] || "—")}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D11D21] flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#D11D21] animate-pulse" /> {t("contact.closedTuesdays")}
            </p>
          </div>

          <div className="glass-card p-8">
            <h3 className="font-serif-display text-2xl text-[#2A0708] mb-5">{t("contact.connect")}</h3>
            <div className="space-y-3">
              {links.map(({ label, url, Icon, color }, i) => {
                const isDisabled = !url;
                const Wrapper = isDisabled ? motion.div : motion.a;
                const wrapperProps = isDisabled ? {} : { href: url, target: "_blank", rel: "noreferrer", whileHover: { x: 4 } };
                return (
                  <Wrapper key={label}
                    initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className={`flex items-center gap-4 p-4 rounded-xl border border-[#E8D9BE] transition-all ${isDisabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#C9A227]/50 hover:bg-[#E8D9BE]/30"}`}
                    data-testid={`linktree-${label.toLowerCase().replace(' ', '-')}`}
                    {...wrapperProps}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}22`, color }}>
                      <Icon size={18} />
                    </div>
                    <span className="text-[#2A0708] font-medium">{label}</span>
                    {isDisabled ? (
                      <span className="ml-auto text-[10px] uppercase tracking-widest text-[#8E5F65]">Not set</span>
                    ) : (
                      <ExternalLink size={14} className="ml-auto text-[#C9A227]" />
                    )}
                  </Wrapper>
                );
              })}
            </div>
          </div>
        </div>

        {s.google_maps_embed && (
          <div className="mt-10 max-w-5xl mx-auto glass-card overflow-hidden" data-testid="map-embed">
            <iframe src={s.google_maps_embed} width="100%" height="420" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Tulsi Restaurant Location" />
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, href, testId }) {
  const Content = (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#E8D9BE] flex-shrink-0">
        <Icon size={16} className="text-[#C9A227]" />
      </div>
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#D11D21]">{label}</p>
        <p className="text-[#2A0708]">{value || "—"}</p>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80">{Content}</a> : Content;
}
